/**
 * isSafeExternalHref — guard for `<a href>` props that render a stored
 * external URL (e.g. `auditions.external_link`, sourced from an LLM
 * extraction of a third-party page, an LLM "correction" with no human in the
 * loop, or a manual admin edit).
 *
 * Rejects `javascript:`, `data:`, `vbscript:`, and any other scheme a
 * `<a href>` would execute instead of navigate — `rel="noopener noreferrer"`
 * does NOT mitigate a `javascript:` href. Same shape as `isRenderableImageSrc`
 * (safe-image-src.ts) but for an anchor href, not an image src: no
 * bucket-relative-path allowance, since an external apply link is never
 * relative to this app.
 *
 * This is the LAST line of defence, not the primary guard — the primary fix
 * is the schema-level `httpUrlSchema` refinement in
 * `app/admin/audition-imports/_lib/schemas/extracted-candidate.schema.ts`,
 * which every path that can set `external_link` already passes through. This
 * guard exists so a future write path that bypasses that schema (a direct
 * SQL edit, a different admin tool, a backfill script) still can't reach a
 * public page as an executable href.
 */
export function isSafeExternalHref(
  href: string | null | undefined,
): href is string {
  if (!href) return false;
  try {
    const protocol = new URL(href).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}
